import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, ExternalLink, ChevronRight, ChevronDown, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { crmUrl } from "../lib/crmUrl";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

// =============================================================================
// Sales → Proposals (КП)
// =============================================================================
// Proposals are built and sent in the partner CRM's calculator; this page reads
// them from the mirror (`GET /api/sales-crm/proposals`, backend/crm_proposals.py):
// who sends what and how often, the discounts (shown vs real), the approvals,
// what the client did with the link, how the deal ended, and the price list.
//
// Series colours are fixed hex from the validated categorical palette, not
// Tailwind scales — every Tailwind scale is re-mapped in the light theme here.
// Colour follows the manager (by their place in the full owner list), so a
// filter never repaints the survivors.
//
// Marker: proposals-page-v2 (weekly chart → «КП без движения» + rhythm columns)
// =============================================================================

const SERIES = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#6250d6", "#e34948", "#008300"];
const GOOD = "#0ca30c";
const WARN = "#e0a520";
const BAD = "#d03b3b";

const PRESETS = [
  { key: "30d", label: "30 дней", days: 30 },
  { key: "90d", label: "90 дней", days: 90 },
  { key: "all", label: "Всё время", days: null },
];

const LOSS_LABELS = { postponed: "отложили", no_contact: "пропал контакт", price: "цена", competitor: "конкурент", "—": "без причины" };
const COUNTRY = { UA: "Украина", KZ: "Казахстан", GE: "Грузия", UZ: "Узбекистан", CY: "Кипр", BE: "Бельгия", MD: "Молдова", TR: "Турция", PL: "Польша", DE: "Германия", AE: "ОАЭ" };
const KIND = { base: "база", unit: "за единицу", option: "опция", tier: "ступень", multiplier: "множитель", subscription: "подписка" };
const MILESTONE = { start: "старт", draft: "драфт", mid: "середина", final: "финал" };

const eur = (n) => (n === null || n === undefined ? "—" : "€" + Math.round(n).toLocaleString("ru-RU"));
const keur = (n) => (n >= 10000 ? `€${Math.round(n / 1000)}k` : eur(n));
const pct = (n) => (n === null || n === undefined ? "—" : `${(Math.round(n * 10) / 10).toLocaleString("ru-RU")}%`);
const shortName = (full) => {
  const p = String(full || "").split(" ");
  return p.length > 1 ? `${p[1]} ${p[0][0]}.` : full || "—";
};
const dur = (min) => {
  if (min === null || min === undefined) return "—";
  if (min < 60) return `${Math.round(min)} мин`;
  if (min < 48 * 60) return `${(min / 60).toFixed(1)} ч`;
  return `${Math.round(min / 1440)} дн`;
};
const isoDay = (d) => d.toISOString().slice(0, 10);

function Kpi({ label, value, hint, tone }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-2xl font-semibold tabular-nums" style={tone ? { color: tone } : undefined}>{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
      {hint ? <div className="text-[11px] text-muted-foreground/70 mt-0.5">{hint}</div> : null}
    </div>
  );
}

function Fact({ value, label }) {
  return (
    <div className="border-l-2 border-orange-500 pl-3 py-0.5">
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function FilterSelect({ label, value, options, onChange }) {
  const active = !!value;
  return (
    <label className={`inline-flex items-center h-8 rounded-md border pl-2.5 pr-1 text-xs shrink-0 ${
      active ? "border-orange-500 text-orange-500" : "border-border text-muted-foreground"}`}>
      <span className="mr-1.5 whitespace-nowrap">{label}:</span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value || null)}
        className={`bg-transparent outline-none cursor-pointer max-w-[190px] py-1 pr-1 ${active ? "font-medium" : "text-foreground"}`}>
        <option value="">Все</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

/** Horizontal bars: label · bar · value. Bars share one scale per block. */
function Bars({ rows, fmt = (v) => v, color = "#71717a", labelWidth = "w-44" }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="space-y-1.5">
      {rows.map((r) => (
        <div key={r.key || r.label} className="flex items-center gap-3 text-sm" title={r.title || `${r.label}: ${fmt(r.value)}`}>
          <div className={`${labelWidth} shrink-0 truncate text-muted-foreground`}>{r.label}</div>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <div className="h-3.5 rounded-r" style={{ width: `${Math.max(1, (r.value / max) * 100)}%`, background: r.color || color }} />
            <span className="tabular-nums text-xs whitespace-nowrap">{fmt(r.value)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function Legend({ owners, colorOf }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {owners.map((o) => (
        <span key={o.key} className="inline-flex items-center gap-1.5">
          <i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: colorOf(o.key) }} />{shortName(o.name)}
        </span>
      ))}
    </div>
  );
}

const STALE = {
  expiring: { label: "истекает", tone: WARN, days: (d) => `через ${d} дн.` },
  not_opened: { label: "не открыли", tone: BAD, days: (d) => `${d} дн. после отправки` },
  no_reply: { label: "тишина", tone: WARN, days: (d) => `${d} дн. без движения` },
  expired: { label: "истёк", tone: "#71717a", days: (d) => (d ? `${d} дн. назад` : "сегодня") },
};

/** Live proposals that went quiet — the list a sales lead acts on today. */
function StaleList({ stale }) {
  const items = stale?.items || [];
  if (!items.length) {
    return <p className="text-sm text-muted-foreground">Все живые КП в движении.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <tbody>
          {items.map((x) => {
            const st = STALE[x.reason];
            const url = crmUrl("deal", x.deal_id);
            return (
              <tr key={x.id} className="border-t border-border first:border-t-0">
                <td className="py-2 pr-3 whitespace-nowrap">
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs" style={{ color: st.tone, boxShadow: `inset 0 0 0 1px ${st.tone}` }}>{st.label}</span>
                </td>
                <td className="pr-3 min-w-[200px]">
                  {url ? (
                    <a href={url} target="_blank" rel="noreferrer" className="hover:text-orange-500 inline-flex items-center gap-1">
                      {x.company || "—"}<ExternalLink className="h-3 w-3 opacity-50" />
                    </a>
                  ) : (x.company || "—")}
                  {x.project ? <span className="text-[11px] text-muted-foreground ml-2">{x.project}</span> : null}
                </td>
                <td className="pr-3 whitespace-nowrap text-muted-foreground">{shortName(x.owner_name)}</td>
                <td className="pr-3 text-right tabular-nums whitespace-nowrap">{eur(x.sell)}</td>
                <td className="text-right whitespace-nowrap text-xs text-muted-foreground tabular-nums">{st.days(x.days)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** One dot per proposal on a real-discount axis, one row per manager. */
function DiscountDots({ offers, owners, colorOf, gate }) {
  const vals = offers.map((o) => o.real_discount_pct);
  const lo = Math.floor(Math.min(-5, ...vals) / 5) * 5;
  const hi = Math.ceil(Math.max(gate + 5, ...vals) / 5) * 5;
  const W = 700, L = 120, R = 16, rowH = 28, H = 40 + owners.length * rowH;
  const x = (v) => L + ((v - lo) / (hi - lo)) * (W - L - R);
  const ticks = [];
  for (let v = lo; v <= hi; v += 5) ticks.push(v);
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[480px]" role="img">
        {ticks.map((v) => (
          <g key={v}>
            <line x1={x(v)} x2={x(v)} y1={8} y2={H - 22} className="stroke-border" strokeWidth={v === 0 ? 1.5 : 0.6} />
            <text x={x(v)} y={H - 6} textAnchor="middle" className="fill-muted-foreground" fontSize="11">{v}%</text>
          </g>
        ))}
        <line x1={x(gate)} x2={x(gate)} y1={8} y2={H - 22} stroke={WARN} strokeDasharray="4 3" />
        <text x={x(gate) + 4} y={16} fontSize="11" fill={WARN}>порог {gate}%</text>
        {owners.map((o, i) => {
          const cy = 34 + i * rowH;
          return (
            <g key={o.key}>
              <text x={0} y={cy + 4} fontSize="12" className="fill-muted-foreground">{shortName(o.name)}</text>
              {offers.filter((r) => r.owner === o.key).map((r, j) => (
                <circle key={r.id} cx={x(r.real_discount_pct)} cy={cy + ((j % 3) - 1) * 5} r={5.5}
                  fill={colorOf(o.key)} className="stroke-card" strokeWidth={2}>
                  <title>{`${r.company}: прайс ${eur(r.list)} → ${eur(r.sell)} · реальная ${pct(r.real_discount_pct)} · в КП ${pct(r.shown_discount_pct)}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function MarginHistogram({ offers, red, green }) {
  const bins = [];
  for (let b = 10; b < 60; b += 5) bins.push({ b, n: offers.filter((o) => o.margin_pct !== null && o.margin_pct >= b && o.margin_pct < b + 5).length });
  const used = bins.filter((x, i) => x.n || bins.slice(0, i).some((y) => y.n) && bins.slice(i).some((y) => y.n));
  const max = Math.max(1, ...used.map((x) => x.n));
  return (
    <div>
      <div className="flex items-end gap-1.5 h-36 border-b border-border pt-4">
        {used.map((x) => (
          <div key={x.b} className="flex-1 flex flex-col items-center justify-end h-full" title={`маржа ${x.b}–${x.b + 5}%: ${x.n} КП`}>
            {x.n ? <div className="text-[11px] tabular-nums mb-1">{x.n}</div> : null}
            <div className="w-full rounded-t" style={{ height: `${(x.n / max) * 85}%`, background: x.b < red ? BAD : x.b < green ? WARN : GOOD }} />
          </div>
        ))}
      </div>
      <div className="flex gap-1.5 mt-1">
        {used.map((x) => <div key={x.b} className="flex-1 text-center text-[11px] text-muted-foreground">{x.b}%</div>)}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-3">
        <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm" style={{ background: BAD }} />ниже {red}%</span>
        <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm" style={{ background: WARN }} />{red}–{green}%</span>
        <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm" style={{ background: GOOD }} />{green}% и выше</span>
      </div>
    </div>
  );
}

/** Manager × product: cell shade = share of the manager's own sales. */
function ProductMatrix({ matrix, owners, products }) {
  const cols = products.slice(0, 8);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-muted-foreground">
            <th className="text-left font-normal py-1.5 pr-2">Менеджер</th>
            {cols.map((p) => <th key={p.product} className="font-normal px-1 py-1.5 text-center min-w-[72px]">{p.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {owners.map((o) => {
            const row = matrix[o.key] || {};
            const tot = Object.values(row).reduce((a, b) => a + b, 0) || 1;
            return (
              <tr key={o.key} className="border-t border-border">
                <td className="py-1.5 pr-2 whitespace-nowrap">{shortName(o.name)}</td>
                {cols.map((p) => {
                  const v = row[p.product] || 0;
                  const share = v / tot;
                  return (
                    <td key={p.product} className="px-1 py-1">
                      <div className="rounded px-1 py-1.5 text-center tabular-nums"
                        title={`${shortName(o.name)} · ${p.label}: ${eur(v)} (${pct(share * 100)} его продаж)`}
                        style={{ background: v ? `rgba(42,120,214,${0.12 + share * 0.75})` : "transparent", color: share > 0.45 ? "#fff" : undefined }}>
                        {v ? keur(v) : <span className="text-muted-foreground/40">·</span>}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PriceList({ blocks, red, green }) {
  const [open, setOpen] = useState({});
  return (
    <div className="divide-y divide-border border-y border-border">
      {blocks.map((b) => {
        const base = b.items.find((i) => i.kind === "base");
        const isOpen = !!open[b.product];
        return (
          <div key={b.product}>
            <button onClick={() => setOpen({ ...open, [b.product]: !isOpen })}
              className="w-full flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2.5 text-left hover:bg-muted/40 px-1">
              {isOpen ? <ChevronDown className="h-3.5 w-3.5 self-center" /> : <ChevronRight className="h-3.5 w-3.5 self-center" />}
              <span className="font-medium">{b.label}</span>
              <span className="text-xs text-muted-foreground">
                {b.items.length} позиций
                {base && base.price ? ` · база ${eur(base.price)}` : ""}
                {base && base.days ? ` · ${base.days} дн.` : ""}
                {b.schedule.length ? ` · оплата ${b.schedule.map(([k, v]) => `${MILESTONE[k] || k} ${v}%`).join(" / ")}` : ""}
              </span>
            </button>
            {isOpen ? (
              <div className="overflow-x-auto pb-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-muted-foreground">
                      <th className="text-left font-normal py-1 px-2">Позиция</th>
                      <th className="text-left font-normal py-1 px-2">Тип</th>
                      <th className="text-right font-normal py-1 px-2">Цена</th>
                      <th className="text-right font-normal py-1 px-2">Себестоимость</th>
                      <th className="text-right font-normal py-1 px-2">Маржа</th>
                      <th className="text-right font-normal py-1 px-2">Дней</th>
                    </tr>
                  </thead>
                  <tbody>
                    {b.items.map((i) => {
                      const m = i.price && i.cost !== null && i.cost !== undefined ? (1 - i.cost / i.price) * 100 : null;
                      const price = i.kind === "multiplier" ? `×${i.multiplier ?? ""}`
                        : `${eur(i.price || 0)}${i.kind === "tier" && i.range_min !== null && i.range_min !== undefined ? ` (${i.range_min}–${i.range_max ?? "∞"})` : ""}`;
                      return (
                        <tr key={i.key} className="border-t border-border/60">
                          <td className="py-1.5 px-2">{i.label}{i.unit ? <span className="text-muted-foreground"> / {i.unit}</span> : null}</td>
                          <td className="py-1.5 px-2 text-muted-foreground">{KIND[i.kind] || i.kind}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums">{price}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums">{i.cost !== null && i.cost !== undefined ? eur(i.cost) : "—"}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums" style={{ color: m === null ? undefined : m < red ? BAD : m < green ? WARN : GOOD }}>{pct(m)}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums">{i.days || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default function SalesProposalsPage() {
  const [preset, setPreset] = useState("all");
  const [market, setMarket] = useState(null);
  const [owner, setOwner] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = {};
    const p = PRESETS.find((x) => x.key === preset);
    if (p && p.days) params.date_from = isoDay(new Date(Date.now() - p.days * 86400000));
    if (market) params.market = market;
    if (owner) params.owner = owner;
    try {
      const res = await api.get("/sales-crm/proposals", { params });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Не удалось загрузить КП");
    } finally {
      setLoading(false);
    }
  }, [preset, market, owner]);

  useEffect(() => { load(); }, [load]);

  // colour follows the person, by their place in the full owner list
  const allOwners = useMemo(() => data?.filters?.owners || [], [data]);
  const colorOf = useCallback((key) => {
    const i = allOwners.findIndex((o) => o.key === key);
    return SERIES[(i < 0 ? allOwners.length : i) % SERIES.length];
  }, [allOwners]);

  if (error) {
    return (
      <div className="p-6 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-2 rounded-lg border border-rose-500/40 bg-rose-500/5 px-3 py-2 text-sm">
          <AlertTriangle className="h-4 w-4 text-rose-500" /> {String(error)}
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }
  if (data.empty) {
    return (
      <div className="p-6 max-w-[1400px] mx-auto space-y-2">
        <h1 className="text-2xl font-semibold">КП</h1>
        <p className="text-sm text-muted-foreground">
          КП из CRM ещё не синхронизированы. Зеркало подтягивает их раз в 30 минут — загляните позже.
        </p>
      </div>
    );
  }

  const s = data.summary;
  const S = data.settings || {};
  const gate = Number(S["co.discount_gate"] || 10);
  const red = Number(S["co.margin_red"] || 20);
  const green = Number(S["co.margin_green"] || 30);
  const published = data.offers.filter((o) => o.status === "published");
  const owners = data.by_manager.map((m) => ({ key: m.key, name: m.name }));
  const A = data.approvals;
  const maxMgrTotal = Math.max(1, ...data.by_manager.map((m) => m.total));

  return (
    <div className="p-6 pt-0 space-y-4 max-w-[1400px] mx-auto">
      <div className="pt-6 pb-1">
        <h1 className="text-2xl font-semibold">КП</h1>
        <p className="text-sm text-muted-foreground">
          Коммерческие предложения из CRM: кто, когда и что отправляет, скидки, утверждения и ответ клиента. Суммы в евро.
        </p>
      </div>

      <div className="sticky top-0 z-30 -mx-6 px-6 py-2.5 bg-background/95 backdrop-blur border-y border-border flex flex-wrap items-center gap-2">
        <div className="flex rounded-md border border-border overflow-hidden shrink-0">
          {PRESETS.map((p) => (
            <button key={p.key} onClick={() => setPreset(p.key)}
              className={`px-2.5 py-1.5 text-xs transition ${preset === p.key ? "bg-orange-500/15 text-orange-500 font-medium" : "hover:bg-muted/60"}`}>
              {p.label}
            </button>
          ))}
        </div>
        <FilterSelect label="Рынок" value={market} onChange={setMarket}
          options={(data.filters?.markets || []).map((m) => ({ value: m.key, label: m.name }))} />
        <FilterSelect label="Менеджер" value={owner} onChange={setOwner}
          options={allOwners.map((o) => ({ value: o.key, label: o.name }))} />
        <Button variant="ghost" size="sm" className="ml-auto h-8" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="proposals-kpis">
        <Kpi label="КП отправлено" value={s.sent} hint={s.drafts ? `+${s.drafts} черновиков` : null} />
        <Kpi label="На сумму" value={keur(s.total)} hint={`средний чек ${keur(s.avg)}`} />
        <Kpi label="Открыли" value={s.opened} hint={s.sent ? `${pct((s.opened / s.sent) * 100)} · повторно ${s.opened_repeat}` : null} />
        <Kpi label="Время до открытия" value={dur(s.median_minutes_to_open)} hint="медиана от первой отправки" />
        <Kpi label="Принято в КП" value={s.accepted} tone={GOOD} />
        <Kpi label="Выиграно / проиграно" value={`${s.won} / ${s.lost}`} hint={`${s.in_progress} в работе`} />
        <Kpi label="Скидка: в КП / реальная" value={`${pct(s.median_shown_discount)} / ${pct(s.median_real_discount)}`} hint="медиана" />
        <Kpi label="Маржа" value={pct(s.median_margin)} hint={`медиана · зелёная от ${green}%`}
          tone={s.median_margin === null ? undefined : s.median_margin < red ? BAD : s.median_margin < green ? WARN : GOOD} />
      </div>

      <Card data-testid="proposals-stale">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex flex-wrap items-baseline gap-x-2">
            КП без движения
            <span className="font-normal text-muted-foreground tabular-nums">
              {data.stale?.items?.length || 0} · {keur(data.stale?.total || 0)}
            </span>
            <span className="ml-auto text-[11px] font-normal text-muted-foreground/60">
              не открыли {data.stale?.rules?.not_opened_days}+ дн. · тишина {data.stale?.rules?.no_reply_days}+ дн. · истекает в {data.stale?.rules?.expiring_days} дн.
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent><StaleList stale={data.stale} /></CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Воронка КП</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Bars fmt={(v) => `${v} · ${s.sent ? pct((v / s.sent) * 100) : "—"}`} color="#d9541c" rows={[
              { label: "Отправлено", value: s.sent },
              { label: "Открыто вне CRM", value: s.opened },
              { label: "Смотрели примеры работ", value: s.examples },
              { label: "Принято в КП", value: s.accepted },
              { label: "Сделка выиграна", value: s.won, color: GOOD },
            ]} />
            <p className="text-[11px] text-muted-foreground/70">
              «Вне CRM» — открытие без входа в аккаунт CRM; часть может быть самим менеджером с телефона.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">По рынкам</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {data.by_market.map((m) => (
                <button key={m.key} onClick={() => setMarket(market === m.key ? null : m.key)}
                  className={`rounded-lg border p-3 text-left transition ${market === m.key ? "border-orange-500 bg-orange-500/10" : "border-border hover:border-orange-500/60"}`}>
                  <div className="text-xs text-muted-foreground">{m.name}</div>
                  <div className="text-xl font-semibold tabular-nums">{keur(m.total)}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 tabular-nums">
                    {m.sent} КП · открыто {m.opened} · won {m.won} / lost {m.lost}
                  </div>
                  <div className="text-[11px] text-muted-foreground tabular-nums">
                    скидка {pct(m.shown_discount)} / реальная {pct(m.real_discount)} · маржа {pct(m.margin)}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Менеджеры</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th className="text-left font-normal py-1.5 pr-2">Менеджер</th>
                <th className="text-right font-normal px-2">КП</th>
                <th className="text-right font-normal px-2">В неделю</th>
                <th className="text-right font-normal px-2">Последнее</th>
                <th className="text-left font-normal px-2 min-w-[160px]">Сумма</th>
                <th className="text-right font-normal px-2">Скидка в КП</th>
                <th className="text-right font-normal px-2">Реальная</th>
                <th className="text-right font-normal px-2">Версий</th>
                <th className="text-right font-normal px-2">Открыли</th>
                <th className="text-right font-normal px-2">Принято</th>
                <th className="text-right font-normal px-2">Утвержд.</th>
                <th className="text-right font-normal px-2">Маржа</th>
                <th className="text-right font-normal pl-2">Won / Lost</th>
              </tr>
            </thead>
            <tbody>
              {data.by_manager.map((m) => (
                <tr key={m.key} className="border-t border-border">
                  <td className="py-2 pr-2 whitespace-nowrap">
                    <button onClick={() => setOwner(owner === m.key ? null : m.key)} className="inline-flex items-center gap-2 hover:text-orange-500">
                      <i className="h-2.5 w-2.5 rounded-sm" style={{ background: colorOf(m.key) }} />{m.name}
                    </button>
                    <span className="ml-2 text-[11px] text-muted-foreground">{m.market}</span>
                  </td>
                  <td className="px-2 text-right tabular-nums">{m.sent}</td>
                  <td className="px-2 text-right tabular-nums">{m.per_week}</td>
                  <td className="px-2 text-right tabular-nums whitespace-nowrap" style={{ color: m.last_sent_days >= 14 ? BAD : m.last_sent_days >= 7 ? WARN : undefined }}>
                    {m.last_sent_days === null ? "—" : m.last_sent_days === 0 ? "сегодня" : `${m.last_sent_days} дн.`}
                  </td>
                  <td className="px-2">
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 rounded-r" style={{ width: `${(m.total / maxMgrTotal) * 100}px`, background: colorOf(m.key) }} />
                      <span className="tabular-nums text-xs">{keur(m.total)}</span>
                    </div>
                  </td>
                  <td className="px-2 text-right tabular-nums">{pct(m.shown_discount)}</td>
                  <td className="px-2 text-right tabular-nums">{pct(m.real_discount)}</td>
                  <td className="px-2 text-right tabular-nums">{m.versions}</td>
                  <td className="px-2 text-right tabular-nums">{m.opened}/{m.sent}</td>
                  <td className="px-2 text-right tabular-nums">{m.accepted}</td>
                  <td className="px-2 text-right tabular-nums">{m.approvals}</td>
                  <td className="px-2 text-right tabular-nums" style={{ color: m.margin === null ? undefined : m.margin < red ? BAD : m.margin < green ? WARN : GOOD }}>{pct(m.margin)}</td>
                  <td className="pl-2 text-right tabular-nums">{m.won} / {m.lost}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Кто что продаёт</CardTitle></CardHeader>
        <CardContent>
          <ProductMatrix matrix={data.product_matrix} owners={owners} products={data.products} />
          <p className="text-[11px] text-muted-foreground/70 mt-2">Насыщенность — доля продукта в КП этого менеджера.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Скидки</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Fact value={pct(s.median_shown_discount)} label="медианная скидка, которую видит клиент" />
            <Fact value={pct(s.median_real_discount)} label="медианная реальная скидка к прайсу" />
            <Fact value={`${s.lines_marked_up} из ${s.lines}`} label="позиций с ценой выше прайса, от которой дана «скидка»" />
            <Fact value={s.above_price_list} label="КП в итоге дороже прайса" />
          </div>
          <Legend owners={owners} colorOf={colorOf} />
          <DiscountDots offers={published} owners={owners} colorOf={colorOf} gate={gate} />
          <p className="text-[11px] text-muted-foreground/70">Точка — КП. Левее нуля — продано дороже прайса. Реальная скидка = 1 − итог / прайс.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Утверждения скидок выше {gate}%</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Fact value={A.total} label="запросов" />
            <Fact value={`${A.by_status?.approved || 0} из ${A.total}`} label={A.total && (A.by_status?.approved || 0) === A.total ? "одобрено, отказов нет" : "одобрено"} />
            <Fact value={(A.deciders_named || []).map(shortName).join(", ") || "—"} label="утверждает" />
            <Fact value={A.median_seconds === null ? "—" : A.median_seconds < 60 ? `${Math.round(A.median_seconds)} с` : dur(A.median_seconds / 60)} label={`медиана решения · ${A.under_minute} быстрее минуты`} />
            <Fact value={pct(A.max_pct)} label="максимальная одобренная скидка на позицию" />
          </div>
          {Object.keys(A.by_requester || {}).length ? (
            <Bars labelWidth="w-36" rows={Object.entries(A.by_requester).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({
              key: k, label: shortName(allOwners.find((o) => o.key === k)?.name || k), value: v, color: colorOf(k),
            }))} fmt={(v) => `${v} запрос${v === 1 ? "" : v < 5 ? "а" : "ов"}`} />
          ) : null}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Маржа КП</CardTitle></CardHeader>
          <CardContent><MarginHistogram offers={published} red={red} green={green} /></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Что продаём</CardTitle></CardHeader>
          <CardContent>
            <Bars color="#2a78d6" fmt={keur} rows={data.products.map((p) => ({
              key: p.product, label: p.label, value: p.sell,
              title: `${p.label}: ${eur(p.sell)} в ${p.offers} КП · маржа ${pct(p.margin)}`,
            }))} />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Чем закончились сделки</CardTitle></CardHeader>
          <CardContent>
            <Bars rows={[
              { label: "Выиграно", value: data.outcomes.won, color: GOOD },
              ...Object.entries(data.outcomes.open_by_stage).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ key: k, label: `В работе: ${k}`, value: v, color: "#71717a" })),
              ...Object.entries(data.outcomes.lost_by_reason).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ key: `l-${k}`, label: `Проиграно: ${LOSS_LABELS[k] || k}`, value: v, color: BAD })),
            ]} labelWidth="w-52" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Откуда открывают</CardTitle></CardHeader>
          <CardContent>
            <Bars color="#1baf7a" labelWidth="w-32" fmt={(v) => `${v} КП`}
              rows={Object.entries(data.countries).map(([k, v]) => ({ key: k, label: COUNTRY[k] || k, value: v }))} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Все КП · {data.offers.length}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th className="text-left font-normal py-1.5 pr-2">Дата</th>
                <th className="text-left font-normal px-2">Клиент</th>
                <th className="text-left font-normal px-2">Менеджер</th>
                <th className="text-right font-normal px-2">Сумма</th>
                <th className="text-right font-normal px-2">Скидка в КП</th>
                <th className="text-right font-normal px-2">Реальная</th>
                <th className="text-right font-normal px-2">Маржа</th>
                <th className="text-right font-normal px-2">Версий</th>
                <th className="text-right font-normal px-2">Открытий</th>
                <th className="text-left font-normal pl-2">Статус</th>
              </tr>
            </thead>
            <tbody>
              {data.offers.map((o) => {
                const url = crmUrl("deal", o.deal_id);
                return (
                  <tr key={o.id} className="border-t border-border align-top">
                    <td className="py-2 pr-2 tabular-nums whitespace-nowrap text-muted-foreground">
                      {new Date(o.published_at || o.created_at).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" })}
                    </td>
                    <td className="px-2 min-w-[200px]">
                      {url ? (
                        <a href={url} target="_blank" rel="noreferrer" className="hover:text-orange-500 inline-flex items-center gap-1">
                          {o.company || "—"}<ExternalLink className="h-3 w-3 opacity-50" />
                        </a>
                      ) : (o.company || "—")}
                      {o.project ? <div className="text-[11px] text-muted-foreground">{o.project}</div> : null}
                    </td>
                    <td className="px-2 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm" style={{ background: colorOf(o.owner) }} />{shortName(o.owner_name)}</span>
                    </td>
                    <td className="px-2 text-right tabular-nums">{eur(o.sell)}</td>
                    <td className="px-2 text-right tabular-nums">{pct(o.shown_discount_pct)}</td>
                    <td className="px-2 text-right tabular-nums">{pct(o.real_discount_pct)}</td>
                    <td className="px-2 text-right tabular-nums" style={{ color: o.margin_pct === null ? undefined : o.margin_pct < red ? BAD : o.margin_pct < green ? WARN : GOOD }}>{pct(o.margin_pct)}</td>
                    <td className="px-2 text-right tabular-nums">{o.versions}</td>
                    <td className="px-2 text-right tabular-nums">{o.opens}</td>
                    <td className="pl-2 whitespace-nowrap text-xs">
                      {o.status === "draft" ? <span className="text-muted-foreground">черновик</span>
                        : o.stage_kind === "won" ? <span style={{ color: GOOD }}>выиграно</span>
                        : o.stage_kind === "lost" ? <span style={{ color: BAD }}>проиграно{o.loss_reason ? ` · ${LOSS_LABELS[o.loss_reason] || o.loss_reason}` : ""}</span>
                        : <span className="text-muted-foreground">{o.stage || "—"}</span>}
                      {o.accepted ? <span className="ml-1.5" style={{ color: GOOD }}>· принято</span> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Прайс калькулятора</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Fact value={`${gate}%`} label="скидка на позицию, выше которой нужно утверждение" />
            <Fact value={(A.deciders_named || []).map(shortName).join(", ") || "—"} label="утверждает скидки" />
            <Fact value={`${red}% / ${green}%`} label="границы красной и зелёной маржи" />
            <Fact value={`${S["co.valid_days"] || 14} дн.`} label="срок действия КП по умолчанию" />
          </div>
          <PriceList blocks={data.price_list} red={red} green={green} />
        </CardContent>
      </Card>
    </div>
  );
}
